import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { YmsAlertDialog } from '@/components/dashboard/YmsAlertDialog';
import { useToast } from '@/components/ui';
import { IMAGE_CONSTANTS } from '@/constants';
import {
  useCompleteTaskMutation,
  useFetchTrailerListMutation,
  useFetchLocationListMutation,
} from '@/hooks/dashboard';
import { useAppSelector } from '@/store';

import type {
  TaskDetail,
  QuestionFlowNode,
  TrailerInfo,
  LocationInfo,
  CompleteFlowAnswers,
  ExceptionItem,
  OverrideReasonCodeInfo,
} from '@/types';

// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────

type QuestionSelection = 'not_selected' | 'yes' | 'no';

interface DisplayedQuestion {
  node: QuestionFlowNode;
  selection: QuestionSelection;
}

interface CompleteTaskDialogProps {
  active: boolean;
  task: TaskDetail | null;
  rootStep: QuestionFlowNode | null;
  elapsedSeconds: number;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

/** Parse COMPLETE_ACTION_EXCEPTION from config settings */
function parseExceptionList(
  settings: Record<string, string | number | boolean> | null,
  taskType: string
): ExceptionItem[] {
  if (!settings?.['COMPLETE_ACTION_EXCEPTION']) return [];
  try {
    const parsed = JSON.parse(String(settings['COMPLETE_ACTION_EXCEPTION'])) as Record<
      string,
      ExceptionItem[]
    >;
    if (taskType === 'Move') return parsed['Move'] ?? parsed['move'] ?? [];
    return parsed['Audit'] ?? parsed['audit'] ?? [];
  } catch {
    return [];
  }
}

/** Parse TRAILER_MASTER_DATA → override reason codes */
function parseOverrideReasonCodes(
  settings: Record<string, string | number | boolean> | null
): OverrideReasonCodeInfo[] {
  if (!settings?.['TRAILER_MASTER_DATA']) return [];
  try {
    const parsed = JSON.parse(String(settings['TRAILER_MASTER_DATA'])) as {
      OverrideReasonCodeInfo?: OverrideReasonCodeInfo[];
    };
    return parsed.OverrideReasonCodeInfo ?? [];
  } catch {
    return [];
  }
}

/** Parse MOVE_RESTRICTION_LIMIT → time limit in seconds */
function parseMoveRestrictionLimit(
  settings: Record<string, string | number | boolean> | null,
  sourceType: string,
  destType: string
): number {
  if (!settings?.['MOVE_RESTRICTION_LIMIT']) return 0;
  try {
    const parsed = JSON.parse(String(settings['MOVE_RESTRICTION_LIMIT'])) as {
      MoveRestrictionTimeLimitInfo?: {
        SourceType: string;
        DestinationType: string;
        TimeLimit: number;
      }[];
    };
    const list = parsed.MoveRestrictionTimeLimitInfo ?? [];
    const match = list.find((r) => r.SourceType === sourceType && r.DestinationType === destType);
    return match?.TimeLimit ?? 0;
  } catch {
    return 0;
  }
}

// ─────────────────────────────────────────────────────────────────
// Xamarin colors (from App.xaml)
// ─────────────────────────────────────────────────────────────────

const COLORS = {
  primary: '#003366', // PrimaryBackgroundColor
  taskDetailsText: '#243743', // TaskDetailsTextColor
  exceptionHeader: '#0D262E', // CompleteTaskExceptionHeaderColor
  completeGreen: '#2FA851', // CompleteButtonGreenColor
  frameGray: '#D0D0D0', // TrailerListFrameColor
  exceptionOrange: '#FFA319', // ExceptionTextColor
  negativeRed: '#FF2E2E', // NegativeExceptionCode
  disabled: '#aaaaaa', // DisabledBackgroundColor
  blueButton: '#1357A0', // BlueButtonBackgroundColor
  white: '#FFFFFF',
} as const;

// ─────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────

export function CompleteTaskDialog({
  active,
  task,
  rootStep,
  elapsedSeconds,
  onClose,
}: CompleteTaskDialogProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const completeMutation = useCompleteTaskMutation();
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const trailerMutation = useFetchTrailerListMutation();
  const locationMutation = useFetchLocationListMutation();
  const settings = useAppSelector((state) => state.global.settings) as Record<
    string,
    string | number | boolean
  > | null;

  // ── Question flow state ──
  const [displayedQuestions, setDisplayedQuestions] = useState<DisplayedQuestion[]>([]);
  const [enableComplete, setEnableComplete] = useState(false);
  const [isOtherQuestionsCompleted, setIsOtherQuestionsCompleted] = useState(false);

  // ── Trailer state ──
  const [selectedTrailer, setSelectedTrailer] = useState<TrailerInfo | null>(null);
  const [trailerSearchText, setTrailerSearchText] = useState('');
  const [trailerDropdownOpen, setTrailerDropdownOpen] = useState(false);
  const [isOtherTrailer, setIsOtherTrailer] = useState(false);
  const [otherTrailerText, setOtherTrailerText] = useState('');
  const [otherCarrierText, setOtherCarrierText] = useState('');

  // ── Location state ──
  const [selectedLocation, setSelectedLocation] = useState<LocationInfo | null>(null);
  const [locationSearchText, setLocationSearchText] = useState('');
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  // ── Rename trailer state ──
  const [trailerNumber, setTrailerNumber] = useState('');
  const [completeRenameTrailerNumber, setCompleteRenameTrailerNumber] = useState(false);
  const [showRenameTrailerError, setShowRenameTrailerError] = useState(false);
  const [renameTrailerErrorText, setRenameTrailerErrorText] = useState('');

  // ── Exception state ──
  const [exceptionCode, setExceptionCode] = useState('');
  const [exceptionDescription, setExceptionDescription] = useState('');

  // ── Comments state ──
  const [taskComments, setTaskComments] = useState('');
  const [trailerComments, setTrailerComments] = useState('');

  // ── Override reason state (Move tasks only) ──
  const [isEnableOverrideReasonCode, setIsEnableOverrideReasonCode] = useState(false);
  const [selectedOverrideReason, setSelectedOverrideReason] =
    useState<OverrideReasonCodeInfo | null>(null);
  const [overrideReasonSearchText, setOverrideReasonSearchText] = useState('');
  const [overrideReasonDropdownOpen, setOverrideReasonDropdownOpen] = useState(false);

  // ── Timer ref for move restriction ──
  const moveRestrictionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Parsed config data ──
  const exceptionList = useMemo(
    () => parseExceptionList(settings, task?.TaskType ?? ''),
    [settings, task?.TaskType]
  );

  const overrideReasonCodes = useMemo(() => parseOverrideReasonCodes(settings), [settings]);

  // ── Filtered lists ──
  const trailersWithOther = useMemo<TrailerInfo[]>(() => {
    const raw = trailerMutation.data ?? [];
    const otherItem: TrailerInfo = {
      TrailerName: t('completeTask.otherTrailer'),
      TrailerId: '__OTHER__',
      CarrierName: '',
      CarrierCompanyName: '',
    };
    return [otherItem, ...raw];
  }, [trailerMutation.data, t]);

  const filteredTrailers = useMemo(() => {
    if (!trailerSearchText.trim()) return trailersWithOther;
    return trailersWithOther.filter((tr) =>
      tr.TrailerName.toUpperCase().includes(trailerSearchText.toUpperCase())
    );
  }, [trailersWithOther, trailerSearchText]);

  const filteredLocations = useMemo(() => {
    const raw = locationMutation.data ?? [];
    if (!locationSearchText.trim()) return raw;
    return raw.filter((loc) => {
      const display = `${loc.LocationZone} - ${loc.LocationName} (${loc.LocationType})`;
      return display.toUpperCase().includes(locationSearchText.toUpperCase());
    });
  }, [locationMutation.data, locationSearchText]);

  const filteredOverrideReasons = useMemo(() => {
    if (!overrideReasonSearchText.trim()) return overrideReasonCodes;
    return overrideReasonCodes.filter((r) =>
      r.OverrideReasonName.toUpperCase().includes(overrideReasonSearchText.toUpperCase())
    );
  }, [overrideReasonCodes, overrideReasonSearchText]);

  // ─────────────────────────────────────────────────────────────
  // Initialize flow when dialog opens
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!active || !rootStep || !task) return;

    // Reset all state
    setDisplayedQuestions([]);
    setEnableComplete(false);
    setIsOtherQuestionsCompleted(false);
    setSelectedTrailer(null);
    setTrailerSearchText('');
    setTrailerDropdownOpen(false);
    setIsOtherTrailer(false);
    setOtherTrailerText('');
    setOtherCarrierText('');
    setSelectedLocation(null);
    setLocationSearchText('');
    setLocationDropdownOpen(false);
    setTrailerNumber(task.TrailerName);
    setCompleteRenameTrailerNumber(false);
    setShowRenameTrailerError(false);
    setRenameTrailerErrorText('');
    setExceptionCode('');
    setExceptionDescription('');
    setTaskComments(task.TaskComments);
    setTrailerComments(task.TrailerComments);
    setSelectedOverrideReason(null);
    setOverrideReasonSearchText('');
    setOverrideReasonDropdownOpen(false);

    // Determine if override reason is needed (Move tasks only)
    const isMoveTask = task.TaskType === 'Move';
    setIsEnableOverrideReasonCode(isMoveTask);

    // Start move restriction timer (Xamarin: MoveTaskRestrictLimitTimer)
    if (isMoveTask) {
      const restrictionLimit = parseMoveRestrictionLimit(
        settings,
        task.MoveSourceType,
        task.MoveDestinationType
      );
      if (restrictionLimit > 0 && elapsedSeconds <= restrictionLimit) {
        const remaining = restrictionLimit - elapsedSeconds;
        if (moveRestrictionTimerRef.current) {
          clearTimeout(moveRestrictionTimerRef.current);
        }
        moveRestrictionTimerRef.current = setTimeout(() => {
          setIsEnableOverrideReasonCode((prev) => {
            // Only disable if no reason selected yet
            if (!selectedOverrideReason) return false;
            return prev;
          });
        }, remaining * 1000);
      } else {
        // Xamarin: else { IsEnableOverrideReasonCode = false; }
        // All other cases: restriction limit is 0, not configured, or already exceeded
        setIsEnableOverrideReasonCode(false);
      }
    }

    // Process first question
    if (rootStep.QuestionType === 'TRAILER') {
      // Matches Xamarin: ShowTrailerList → ReceiveJsonAndCache throws NoInternetMessage offline
      if (!navigator.onLine) {
        showToast(t('dashboard.noInternet'), { type: 'error' });
        onClose();
        return;
      }
      trailerMutation.mutate(task.TaskId);
      setDisplayedQuestions([{ node: rootStep, selection: 'not_selected' }]);
    } else {
      setDisplayedQuestions([{ node: rootStep, selection: 'not_selected' }]);
    }

    return () => {
      if (moveRestrictionTimerRef.current) {
        clearTimeout(moveRestrictionTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, rootStep, task]);

  // ─────────────────────────────────────────────────────────────
  // Question flow logic — matches Xamarin CompletedTaskPopUpViewModel
  // ─────────────────────────────────────────────────────────────

  /** Reset question list after current index + process next */
  const resetQuestionList = useCallback(
    (questionIndex: number) => {
      setEnableComplete(false);
      setIsOtherQuestionsCompleted(false);
      setDisplayedQuestions((prev) => {
        const trimmed = prev.slice(0, questionIndex + 1);

        // Reset related state if those question types were removed
        const hasTrailer = trimmed.some((q) => q.node.QuestionType === 'TRAILER');
        const hasLocation = trimmed.some((q) => q.node.QuestionType === 'LOCATION');
        const hasException = trimmed.some((q) => q.node.QuestionType === 'EXCEPTION');
        const hasRename = trimmed.some((q) => q.node.QuestionType === 'RENAMETRAILER');

        if (!hasTrailer) {
          setSelectedTrailer(null);
          setIsOtherTrailer(false);
        }
        if (!hasLocation) {
          setSelectedLocation(null);
        }
        if (!hasException) {
          setExceptionDescription('');
          setExceptionCode('');
        }
        if (!hasRename) {
          setTrailerNumber(task?.TrailerName ?? '');
        }

        return trimmed;
      });
    },
    [task]
  );

  /** Check if complete button should be enabled — matches Xamarin SetCompleteTaskOptions */
  const checkEnableComplete = useCallback(
    (otherQuestionsComplete: boolean) => {
      if (!task) return;

      if (isOtherTrailer) {
        return; // handled by otherTrailerChanged
      }

      if (task.TaskType === 'Move') {
        if (selectedOverrideReason) {
          setEnableComplete(isEnableOverrideReasonCode && otherQuestionsComplete);
        } else if (isEnableOverrideReasonCode) {
          setEnableComplete(false);
        } else {
          setEnableComplete(true);
        }
      } else {
        setEnableComplete(!isOtherTrailer);
      }
    },
    [task, isOtherTrailer, selectedOverrideReason, isEnableOverrideReasonCode]
  );

  /** Process the next question node after an answer */
  const loadNextQuestion = useCallback(
    (nextNode: QuestionFlowNode | null) => {
      if (!task) return;

      // Null node means the flow has ended — enable complete
      // Matches Xamarin: when ProcessNextQuestion has no next node, SetCompleteTaskOptions is called
      if (!nextNode) {
        setIsOtherQuestionsCompleted(true);
        checkEnableComplete(true);
        return;
      }

      switch (nextNode.QuestionType) {
        case 'YES/NO':
          setDisplayedQuestions((prev) => [...prev, { node: nextNode, selection: 'not_selected' }]);
          break;

        case 'COMPLETE':
          setIsOtherQuestionsCompleted(true);
          checkEnableComplete(true);
          break;

        case 'LOCATION':
          if (!navigator.onLine) {
            showToast(t('dashboard.noInternet'), { type: 'error' });
            return;
          }
          locationMutation.mutate(task.TaskId);
          setDisplayedQuestions((prev) => [...prev, { node: nextNode, selection: 'not_selected' }]);
          break;

        case 'TRAILER':
          if (!navigator.onLine) {
            showToast(t('dashboard.noInternet'), { type: 'error' });
            return;
          }
          trailerMutation.mutate(task.TaskId);
          setSelectedTrailer(null);
          setIsOtherTrailer(false);
          setOtherTrailerText('');
          setOtherCarrierText('');
          setDisplayedQuestions((prev) => [...prev, { node: nextNode, selection: 'not_selected' }]);
          break;

        case 'EXCEPTION': {
          const code = nextNode.Question;
          setExceptionCode(code);
          const exItem = exceptionList.find((e) => e.code === code);
          setExceptionDescription(exItem?.text ?? '');
          setDisplayedQuestions((prev) => [...prev, { node: nextNode, selection: 'not_selected' }]);
          // Matches Xamarin SetExceptionOptions: follow YesOption, or enable complete if flow ends
          if (nextNode.YesOption) {
            setTimeout(() => {
              loadNextQuestion(nextNode.YesOption);
            }, 0);
          } else {
            setIsOtherQuestionsCompleted(true);
            checkEnableComplete(true);
          }
          break;
        }

        case 'RENAMETRAILER':
          setCompleteRenameTrailerNumber(false);
          setShowRenameTrailerError(false);
          setDisplayedQuestions((prev) => [...prev, { node: nextNode, selection: 'not_selected' }]);
          break;

        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [task, exceptionList, checkEnableComplete]
  );

  // ── YES/NO handler ──
  const handleYesNo = useCallback(
    (index: number, answer: 'yes' | 'no') => {
      const question = displayedQuestions[index];
      if (!question) return;

      // Update selection
      setDisplayedQuestions((prev) => {
        const updated = [...prev];
        const q = updated[index];
        if (q) {
          updated[index] = { ...q, selection: answer };
        }
        return updated;
      });

      // Reset questions after this one
      resetQuestionList(index);

      // Load next based on answer
      const nextNode = answer === 'yes' ? question.node.YesOption : question.node.NoOption;
      // Defer to allow state reset to complete
      setTimeout(() => {
        loadNextQuestion(nextNode);
      }, 0);
    },
    [displayedQuestions, resetQuestionList, loadNextQuestion]
  );

  // ── Trailer selected handler ──
  const handleTrailerSelected = useCallback(
    (trailer: TrailerInfo, questionIndex: number) => {
      setSelectedTrailer(trailer);
      setTrailerDropdownOpen(false);
      setTrailerSearchText('');

      const question = displayedQuestions[questionIndex];
      if (!question) return;

      // Reset questions after this one
      resetQuestionList(questionIndex);

      if (trailer.TrailerId === '__OTHER__') {
        // "Other Trailer" selected
        setIsOtherTrailer(true);
        setOtherTrailerText('');
        setOtherCarrierText('');
        // Follow NoOption
        setTimeout(() => {
          loadNextQuestion(question.node.NoOption);
        }, 0);
      } else {
        setIsOtherTrailer(false);
        // Follow YesOption
        setTimeout(() => {
          loadNextQuestion(question.node.YesOption);
        }, 0);
      }

      // Update exception description for other trailer
      if (trailer.TrailerId === '__OTHER__') {
        setExceptionDescription(t('completeTask.invalidTrailerException'));
      } else {
        const exItem = exceptionList.find((e) => e.code === exceptionCode);
        if (exItem) {
          setExceptionDescription(exItem.text);
        }
      }
    },
    [displayedQuestions, resetQuestionList, loadNextQuestion, t, exceptionList, exceptionCode]
  );

  // ── Location selected handler ──
  const handleLocationSelected = useCallback(
    (location: LocationInfo) => {
      setSelectedLocation(location);
      setLocationDropdownOpen(false);
      setLocationSearchText('');

      // Find the last LOCATION question (manual loop for ES2022 compat)
      let lastLocationIdx = -1;
      for (let i = displayedQuestions.length - 1; i >= 0; i--) {
        if (displayedQuestions[i]?.node.QuestionType === 'LOCATION') {
          lastLocationIdx = i;
          break;
        }
      }
      if (lastLocationIdx < 0) return;

      const question = displayedQuestions[lastLocationIdx];
      if (!question) return;

      resetQuestionList(lastLocationIdx);

      setTimeout(() => {
        loadNextQuestion(question.node.YesOption);
      }, 0);
    },
    [displayedQuestions, resetQuestionList, loadNextQuestion]
  );

  // ── Override reason selected handler ──
  const handleOverrideReasonSelected = useCallback(
    (reason: OverrideReasonCodeInfo) => {
      setSelectedOverrideReason(reason);
      setOverrideReasonDropdownOpen(false);
      setOverrideReasonSearchText('');
      // Re-check enable complete
      if (isOtherQuestionsCompleted) {
        setEnableComplete(isEnableOverrideReasonCode);
      }
    },
    [isOtherQuestionsCompleted, isEnableOverrideReasonCode]
  );

  // ── Other trailer text change handler ──
  const handleOtherTrailerChange = useCallback(() => {
    if (isOtherTrailer) {
      const valid =
        otherTrailerText.trim().length > 0 &&
        /\d/.test(otherTrailerText) &&
        otherCarrierText.trim().length > 0;
      setEnableComplete(valid);
    }
  }, [isOtherTrailer, otherTrailerText, otherCarrierText]);

  useEffect(() => {
    handleOtherTrailerChange();
  }, [handleOtherTrailerChange]);

  // ── Rename trailer submit handler ──
  const handleSubmitTrailerNumber = useCallback(() => {
    const isValid = /\d/.test(trailerNumber) && trailerNumber.trim().length > 0;

    if (isValid && trailerNumber !== (task?.TrailerName ?? '')) {
      setCompleteRenameTrailerNumber(true);
      setShowRenameTrailerError(false);

      // Find the RENAMETRAILER question (manual loop for ES2022 compat)
      let renameIdx = -1;
      for (let i = displayedQuestions.length - 1; i >= 0; i--) {
        if (displayedQuestions[i]?.node.QuestionType === 'RENAMETRAILER') {
          renameIdx = i;
          break;
        }
      }
      if (renameIdx < 0) return;

      const question = displayedQuestions[renameIdx];
      if (!question) return;

      resetQuestionList(renameIdx);

      setTimeout(() => {
        loadNextQuestion(question.node.YesOption);
      }, 0);
    } else if (isValid && trailerNumber === (task?.TrailerName ?? '')) {
      setRenameTrailerErrorText(t('completeTask.renameTrailerErrorText'));
      setShowRenameTrailerError(true);
    } else {
      setRenameTrailerErrorText(t('completeTask.emptyTrailerNumber'));
      setShowRenameTrailerError(true);
    }
  }, [trailerNumber, task, displayedQuestions, resetQuestionList, loadNextQuestion, t]);

  // ── Complete handler — matches Xamarin CompleteCommandHandler ──
  const handleComplete = useCallback(() => {
    if (!task || !enableComplete) return;

    const answers: CompleteFlowAnswers = {
      taskComments,
      trailerComments,
    };

    // Only set optional string fields when they have values (exactOptionalPropertyTypes)
    if (exceptionCode) {
      answers.exceptionCode = exceptionCode;
    }
    if (exceptionDescription) {
      answers.exceptionText = exceptionDescription;
    }
    if (completeRenameTrailerNumber && trailerNumber) {
      answers.renamedTrailer = trailerNumber;
    }

    // Trailer selection
    if (selectedTrailer && selectedTrailer.TrailerId !== '__OTHER__') {
      answers.selectedTrailer = selectedTrailer;
      answers.alternateTrailerValid = 'true';
    } else if (isOtherTrailer) {
      answers.isOtherTrailer = true;
      answers.otherTrailerText = otherTrailerText;
      answers.otherCarrierText = otherCarrierText;
      answers.alternateTrailerValid = 'false';
    } else {
      answers.alternateTrailerValid = 'false';
    }

    // Location selection
    if (selectedLocation) {
      answers.selectedLocation = selectedLocation;
    }

    // Override reason (Move tasks)
    if (task.TaskType === 'Move' && selectedOverrideReason && isEnableOverrideReasonCode) {
      answers.overrideReasonCode = selectedOverrideReason.OverrideReasonCode;
      answers.overrideReasonTaskComment = ` {MOVE RESTRICTION = ${selectedOverrideReason.OverrideReasonCode}}`;
    }

    completeMutation.mutate(
      { task, answers },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (error: Error) => {
          onClose();
          setErrorAlert(error.message);
        },
      }
    );
  }, [
    task,
    enableComplete,
    exceptionCode,
    exceptionDescription,
    taskComments,
    trailerComments,
    completeRenameTrailerNumber,
    trailerNumber,
    selectedTrailer,
    isOtherTrailer,
    otherTrailerText,
    otherCarrierText,
    selectedLocation,
    selectedOverrideReason,
    isEnableOverrideReasonCode,
    completeMutation,
    onClose,
  ]);

  if (!active || !task) {
    return (
      <YmsAlertDialog
        open={errorAlert !== null}
        title={t('createAuditTask.warning')}
        message={errorAlert ?? ''}
        onOk={() => {
          setErrorAlert(null);
        }}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────

  const isMoveTask = task.TaskType === 'Move';
  const isAuditTask = task.TaskType === 'Audit';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {/* Xamarin: WidthRequest="{OnIdiom 700, Phone=400}", HeightRequest="600", CornerRadius="22" */}
      <div
        className="flex h-[600px] max-h-[calc(100vh-2rem)] w-full max-w-[400px] flex-col overflow-hidden bg-white shadow-lg md:max-w-[700px]"
        style={{ borderRadius: 22 }}
      >
        {/* ── Scrollable content ── */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {/* ── Task info header — matches Xamarin Grid Row="0" ── */}
          <div className="mb-4 grid grid-cols-2 gap-x-4 gap-y-3">
            {/* Trailer Number */}
            <div>
              <p
                className="text-xs font-bold"
                style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
              >
                {t('completeTask.trailerNumber')}
              </p>
              <p
                className="text-[10px]"
                style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
              >
                {task.TrailerName || '-'}
              </p>
            </div>

            {/* Source/Dest Location based on task type */}
            {isMoveTask && (
              <div>
                <p
                  className="text-xs font-bold"
                  style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
                >
                  {t('completeTask.sourceLocation')}
                </p>
                <p
                  className="text-[10px]"
                  style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
                >
                  {task.MoveSourceZone} - {task.MoveSourceName} ({task.MoveSourceType})
                </p>
              </div>
            )}

            {isAuditTask && (
              <div>
                <p
                  className="text-xs font-bold"
                  style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
                >
                  {t('completeTask.destLocation')}
                </p>
                <p
                  className="text-[10px]"
                  style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
                >
                  {task.AuditLocationName
                    ? `${task.AuditLocationZone} - ${task.AuditLocationName} (${task.AuditLocationType})`
                    : '-'}
                </p>
              </div>
            )}

            {/* Carrier Name */}
            <div>
              <p
                className="text-xs font-bold"
                style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
              >
                {t('completeTask.carrierName')}
              </p>
              <p
                className="text-[10px]"
                style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
              >
                {task.CarrierCompanyName || '-'}
              </p>
            </div>

            {/* Dest Location for Move tasks (row 1, col 1) */}
            {isMoveTask && (
              <div>
                <p
                  className="text-xs font-bold"
                  style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
                >
                  {t('completeTask.destLocation')}
                </p>
                <p
                  className="text-[10px]"
                  style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
                >
                  {task.MoveDestinationZone} - {task.MoveDestinationName} (
                  {task.MoveDestinationType})
                </p>
              </div>
            )}
          </div>

          {/* Separator — matches Xamarin BoxView */}
          <div className="mb-4 mr-2 h-px" style={{ backgroundColor: COLORS.frameGray }} />

          {/* ── Override Reason Code (Move tasks, above questions on mobile) ── */}
          {isMoveTask && isEnableOverrideReasonCode && (
            <div className="mb-4 px-2">
              <p
                className="mb-1 text-xs"
                style={{ color: COLORS.taskDetailsText, fontFamily: 'Poppins, sans-serif' }}
              >
                {t('completeTask.selectOverrideReasonCode')}
              </p>
              <SearchableDropdown
                isOpen={overrideReasonDropdownOpen}
                onToggle={setOverrideReasonDropdownOpen}
                searchText={overrideReasonSearchText}
                onSearchChange={setOverrideReasonSearchText}
                selectedLabel={selectedOverrideReason?.OverrideReasonName}
                placeholder={t('completeTask.selectFromDropDown')}
                emptyText={t('completeTask.reasonCodeNotFound')}
                items={filteredOverrideReasons}
                renderItem={(item) => (item as OverrideReasonCodeInfo).OverrideReasonName}
                onSelect={(item) => {
                  handleOverrideReasonSelected(item as OverrideReasonCodeInfo);
                }}
              />
            </div>
          )}

          {/* ── Question flow — matches Xamarin DisplayedQuestions ── */}
          {displayedQuestions.map((dq, idx) => (
            <div key={idx} className="mb-3">
              {/* YES/NO question */}
              {dq.node.QuestionType === 'YES/NO' && (
                <div className="px-2 py-1">
                  <p
                    className="mb-2 text-xs"
                    style={{
                      color: COLORS.taskDetailsText,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    {dq.node.Question}
                  </p>
                  <div className="flex gap-6">
                    <button
                      disabled={dq.selection === 'yes'}
                      className="flex h-10 w-[100px] items-center justify-center rounded text-sm"
                      style={
                        dq.selection === 'yes'
                          ? {
                              backgroundColor: COLORS.primary,
                              color: COLORS.white,
                              fontFamily: 'Poppins, sans-serif',
                            }
                          : {
                              backgroundColor: 'transparent',
                              color: COLORS.primary,
                              border: `1px solid ${COLORS.primary}`,
                              fontFamily: 'Poppins, sans-serif',
                            }
                      }
                      onClick={() => {
                        handleYesNo(idx, 'yes');
                      }}
                    >
                      {t('common.yes')}
                    </button>
                    <button
                      className="flex h-10 w-[100px] items-center justify-center rounded text-sm"
                      disabled={dq.selection === 'no'}
                      style={
                        dq.selection === 'no'
                          ? {
                              backgroundColor: COLORS.primary,
                              color: COLORS.white,
                              fontFamily: 'Poppins, sans-serif',
                            }
                          : {
                              backgroundColor: 'transparent',
                              color: COLORS.primary,
                              border: `1px solid ${COLORS.primary}`,
                              fontFamily: 'Poppins, sans-serif',
                            }
                      }
                      onClick={() => {
                        handleYesNo(idx, 'no');
                      }}
                    >
                      {t('common.no')}
                    </button>
                  </div>
                </div>
              )}

              {/* TRAILER question */}
              {dq.node.QuestionType === 'TRAILER' && (
                <div className="px-2 py-1">
                  <p
                    className="mb-1 text-xs"
                    style={{
                      color: COLORS.taskDetailsText,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    {dq.node.Question}
                  </p>

                  {trailerMutation.isPending ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin" style={{ color: COLORS.primary }} />
                    </div>
                  ) : (
                    <SearchableDropdown
                      isOpen={trailerDropdownOpen}
                      onToggle={setTrailerDropdownOpen}
                      searchText={trailerSearchText}
                      onSearchChange={setTrailerSearchText}
                      selectedLabel={selectedTrailer?.TrailerName}
                      placeholder={t('completeTask.selectFromDropDown')}
                      emptyText={t('completeTask.emptyTrailerListMessage')}
                      items={filteredTrailers}
                      renderItem={(item) => (item as TrailerInfo).TrailerName}
                      onSelect={(item) => {
                        handleTrailerSelected(item as TrailerInfo, idx);
                      }}
                    />
                  )}

                  {/* "Other Trailer" text inputs */}
                  {isOtherTrailer && (
                    <div className="mt-5">
                      <div className="flex gap-3">
                        <div>
                          <p
                            className="mb-1 text-xs"
                            style={{
                              color: COLORS.taskDetailsText,
                              fontFamily: 'Poppins, sans-serif',
                            }}
                          >
                            {t('completeTask.enterTrailer')}
                          </p>
                          <div
                            className="w-[150px] rounded-[5px] p-1"
                            style={{ border: `1px solid ${COLORS.frameGray}` }}
                          >
                            <input
                              className="w-full border-none bg-transparent px-1 text-sm outline-none"
                              style={{ fontFamily: 'Poppins, sans-serif' }}
                              maxLength={20}
                              value={otherTrailerText}
                              onChange={(e) => {
                                setOtherTrailerText(e.target.value);
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <p
                            className="mb-1 text-xs"
                            style={{
                              color: COLORS.taskDetailsText,
                              fontFamily: 'Poppins, sans-serif',
                            }}
                          >
                            {t('completeTask.enterCarrier')}
                          </p>
                          <div
                            className="w-[150px] rounded-[5px] p-1"
                            style={{ border: `1px solid ${COLORS.frameGray}` }}
                          >
                            <input
                              className="w-full border-none bg-transparent px-1 text-sm outline-none"
                              style={{ fontFamily: 'Poppins, sans-serif' }}
                              maxLength={30}
                              value={otherCarrierText}
                              onChange={(e) => {
                                setOtherCarrierText(e.target.value);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <p
                        className="mt-1 text-[10px] font-bold"
                        style={{
                          color: COLORS.blueButton,
                          fontFamily: 'Poppins, sans-serif',
                        }}
                      >
                        {t('completeTask.trailerNumberNote')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* LOCATION question */}
              {dq.node.QuestionType === 'LOCATION' && (
                <div className="px-2 py-1">
                  <p
                    className="mb-1 text-xs"
                    style={{
                      color: COLORS.taskDetailsText,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    {dq.node.Question}
                  </p>

                  {locationMutation.isPending ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin" style={{ color: COLORS.primary }} />
                    </div>
                  ) : (
                    <SearchableDropdown
                      isOpen={locationDropdownOpen}
                      onToggle={setLocationDropdownOpen}
                      searchText={locationSearchText}
                      onSearchChange={setLocationSearchText}
                      selectedLabel={
                        selectedLocation
                          ? `${selectedLocation.LocationZone} - ${selectedLocation.LocationName}`
                          : undefined
                      }
                      placeholder={t('completeTask.selectFromDropDown')}
                      emptyText={t('completeTask.emptyLocationList')}
                      items={filteredLocations}
                      renderItem={(item) => {
                        const loc = item as LocationInfo;
                        return `${loc.LocationZone} - ${loc.LocationName} (${loc.LocationType})`;
                      }}
                      onSelect={(item) => {
                        handleLocationSelected(item as LocationInfo);
                      }}
                    />
                  )}
                </div>
              )}

              {/* EXCEPTION display */}
              {dq.node.QuestionType === 'EXCEPTION' && (
                <div className="flex items-center gap-2 px-2 py-1">
                  {!dq.node.Question ? (
                    <>
                      <img src={IMAGE_CONSTANTS.allGood} alt="All Good" className="h-6 w-6" />
                      <p
                        className="text-sm"
                        style={{
                          color: COLORS.completeGreen,
                          fontFamily: 'Poppins, sans-serif',
                        }}
                      >
                        {exceptionDescription || t('completeTask.allGood')}
                      </p>
                    </>
                  ) : (
                    <>
                      <img src={IMAGE_CONSTANTS.exception} alt="Exception" className="h-6 w-6" />
                      <p
                        className="text-sm"
                        style={{
                          color: COLORS.exceptionOrange,
                          fontFamily: 'Poppins, sans-serif',
                        }}
                      >
                        {exceptionDescription}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* RENAMETRAILER */}
              {dq.node.QuestionType === 'RENAMETRAILER' && (
                <div className="px-2 py-1">
                  <p
                    className="mb-1 text-xs"
                    style={{
                      color: COLORS.taskDetailsText,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    {t('completeTask.renameTrailer')}
                  </p>
                  <div className="flex items-end gap-2">
                    <div
                      className="rounded p-0"
                      style={{
                        border: completeRenameTrailerNumber
                          ? '1px solid transparent'
                          : `1px solid ${COLORS.frameGray}`,
                      }}
                    >
                      <input
                        className="w-[150px] border-none bg-transparent px-2 py-1 text-sm outline-none"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                        maxLength={20}
                        readOnly={completeRenameTrailerNumber}
                        value={trailerNumber}
                        onChange={(e) => {
                          setTrailerNumber(e.target.value);
                        }}
                      />
                    </div>
                    {!completeRenameTrailerNumber && (
                      <button onClick={handleSubmitTrailerNumber} className="flex-shrink-0">
                        <img src={IMAGE_CONSTANTS.icSubmit} alt="Submit" className="h-10 w-10" />
                      </button>
                    )}
                  </div>
                  {showRenameTrailerError && (
                    <p
                      className="mt-1 text-[10px]"
                      style={{
                        color: COLORS.negativeRed,
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {renameTrailerErrorText}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* ── Comments section — matches Xamarin Grid Row="3" ── */}
          <div className="mt-4 space-y-4 px-2">
            {/* Task Comments */}
            <div>
              <p
                className="mb-1 text-xs font-medium"
                style={{
                  color: COLORS.exceptionHeader,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {t('completeTask.taskComments')}
              </p>
              <div className="rounded-lg p-0.5" style={{ border: `1px solid ${COLORS.frameGray}` }}>
                <textarea
                  className="h-20 w-full resize-none border-none bg-transparent px-2 py-1 text-xs outline-none"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    color: COLORS.taskDetailsText,
                  }}
                  placeholder={t('completeTask.taskCommentPlaceholder')}
                  maxLength={250}
                  value={taskComments}
                  onChange={(e) => {
                    setTaskComments(e.target.value);
                  }}
                />
              </div>
            </div>

            {/* Trailer Comments (only if trailer exists) */}
            {task.TrailerName && (
              <div>
                <p
                  className="mb-1 text-xs font-medium"
                  style={{
                    color: COLORS.exceptionHeader,
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  {t('completeTask.trailerCommentsLabel')}
                </p>
                <div
                  className="rounded-lg p-0.5"
                  style={{ border: `1px solid ${COLORS.frameGray}` }}
                >
                  <textarea
                    className="h-20 w-full resize-none border-none bg-transparent px-2 py-1 text-xs outline-none"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      color: COLORS.taskDetailsText,
                    }}
                    placeholder={t('completeTask.trailerCommentPlaceholder')}
                    maxLength={250}
                    value={trailerComments}
                    onChange={(e) => {
                      setTrailerComments(e.target.value);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer buttons — matches Xamarin Grid Row="1" ── */}
        <div className="flex shrink-0 justify-end gap-4 px-3 py-2">
          {/* Cancel button */}
          <button
            className="flex h-10 w-[157px] items-center justify-center rounded-[5px] text-xs"
            style={{
              backgroundColor: 'transparent',
              color: COLORS.primary,
              border: `1px solid ${COLORS.primary}`,
              fontFamily: 'Poppins, sans-serif',
            }}
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>

          {/* Complete button */}
          <button
            className="flex h-10 w-[157px] items-center justify-center rounded-[5px] text-xs"
            style={{
              backgroundColor: enableComplete ? COLORS.completeGreen : COLORS.disabled,
              color: COLORS.white,
              fontFamily: 'Poppins, sans-serif',
              cursor: enableComplete ? 'pointer' : 'not-allowed',
            }}
            disabled={!enableComplete || completeMutation.isPending}
            onClick={handleComplete}
          >
            {completeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t('completeTask.completeButtonText')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SearchableDropdown — reusable expandable dropdown matching Xamarin Expander
// ─────────────────────────────────────────────────────────────────

interface SearchableDropdownProps {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  searchText: string;
  onSearchChange: (text: string) => void;
  selectedLabel: string | undefined;
  placeholder: string;
  emptyText: string;
  items: unknown[];
  renderItem: (item: unknown) => string;
  onSelect: (item: unknown) => void;
}

function SearchableDropdown({
  isOpen,
  onToggle,
  searchText,
  onSearchChange,
  selectedLabel,
  placeholder,
  emptyText,
  items,
  renderItem,
  onSelect,
}: SearchableDropdownProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div>
      {/* Header / selected value */}
      <div
        role="button"
        tabIndex={0}
        className="w-full cursor-pointer rounded px-2 py-1"
        style={{ border: `1px solid ${COLORS.frameGray}` }}
        onClick={() => {
          onToggle(!isOpen);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(!isOpen);
          }
        }}
      >
        {isOpen ? (
          <input
            ref={searchInputRef}
            className="w-full border-none bg-transparent text-xs outline-none"
            style={{
              fontFamily: 'Poppins, sans-serif',
              color: COLORS.exceptionHeader,
            }}
            placeholder={placeholder}
            value={searchText}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        ) : (
          <p
            className="text-xs"
            style={{
              fontFamily: 'Poppins, sans-serif',
              color: selectedLabel ? COLORS.exceptionHeader : COLORS.frameGray,
            }}
          >
            {selectedLabel ?? placeholder}
          </p>
        )}
      </div>

      {/* Dropdown list */}
      {isOpen && (
        <div
          className="mt-0 max-h-[200px] w-full overflow-y-auto rounded"
          style={{ border: `1px solid ${COLORS.frameGray}` }}
        >
          {items.length === 0 ? (
            <p
              className="px-4 py-3 text-center text-xs font-semibold"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {emptyText}
            </p>
          ) : (
            items.map((item, idx) => (
              <button
                key={idx}
                className="w-full px-3 py-2 text-left text-xs hover:bg-gray-100"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  color: COLORS.exceptionHeader,
                }}
                onClick={() => {
                  onSelect(item);
                }}
              >
                {renderItem(item)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
